import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../css/TopBar.css";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedModel } from "../redux/modelSlice";
import profileIcon from "../assets/Images/ProfileIcon.png";
import settingIcon from "../assets/icons/settingIcon.png";
import helpIcon from "../assets/icons/helpIcon.png";
import logoutIcon from "../assets/Images/LogoutIcon.png";
import { resetSidebarState, setSelectedMenu } from "../redux/sidebarSlice"; // ✅ add this
import { toggleTheme } from "../redux/themeSlice";
import api from "../api";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";

const SuperadminTopbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const profileRef = useRef(null); // 👈 wrapper ref
  const selectedModel = useSelector((state) => state.model.selectedModel);
  const selectedMenu = useSelector((state) => state.sidebar.selectedMenu);

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [user, setUser] = useState(null);
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const theme = useSelector((state) => state.theme.mode);

  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("darkMode") === "true"
  );

  // ✅ Listen for global refresh
  const refreshKey = useSelector((state) => state.refresh.key);

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem("darkMode", newMode);
      document.documentElement.setAttribute(
        "data-theme",
        newMode ? "dark" : "light"
      );
      return newMode;
    });
  };

  // Map for dropdown pages
  const profileRoutes = {
    "/superadmin/profile": "Profile Settings",
    "/superadmin/getHelpPage": "Get Help",
  };

  // MUST be declared BEFORE useEffect
  const currentPath = location.pathname;

  // 🔥 Auto select menu when in profile settings pages
  useEffect(() => {
    if (profileRoutes[currentPath]) {
      dispatch(setSelectedMenu(profileRoutes[currentPath]));
    }
  }, [currentPath]);

  // If route is profile dropdown, show its title, else show sidebar menu
  const pageTitle = profileRoutes[currentPath] || selectedMenu;

  const handleLogout = async () => {
    try {
      // ✅ Call backend logout API
      await api.post(`${process.env.REACT_APP_API_URL}/auth/logout-device`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      // ✅ Clear localStorage & sessionStorage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");

      // ✅ Reset Redux / UI state
      dispatch(resetSidebarState());

      // ✅ Show success toast
      showSuccessToast(
        "You have been logged out successfully. Have a great day!"
      );

      // ✅ Redirect to login page
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
      // Optional: show toast/error notification to user
      // ❌ Show error toast
      showErrorToast("Logout failed. Please try again.");
    }
  };

  const toggleProfileMenu = () => setShowProfileMenu(!showProfileMenu);
  const closeMenu = () => setShowProfileMenu(false);
  // const toggleDarkMode = () => setDarkMode(!darkMode);

  // 👇 Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProfileMenu]);

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  useEffect(() => {
    document.body.classList.toggle("dark-mode", theme === "dark");
  }, [theme]);

  useEffect(() => {
    if (!selectedModel) {
      dispatch(setSelectedModel("LLaMA3.2"));
    }
  }, [dispatch, selectedModel]);

  // ✅ Fetch user profile from API
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/user-profile/profile`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setUser(res.data);
      } catch (err) {
        console.error("Failed to fetch user profile:", err);
      }
    };

    if (token) fetchUserProfile();
  }, [token, refreshKey]);

  return (
    <div>
      <nav className="topbar navbar navbar-expand-lg px-3 shadow-sm d-flex justify-content-between align-items-center">
        <div className="topbar-left">
          <h4 className="m-0 fw-bold ak-text-xl main-title-no-separator">{selectedMenu}</h4>
        </div>
        <div className="d-flex align-items-center gap-3 ms-auto position-relative">
          <Link
            to="notifications"
            className="btn hi btn-link text-dark p-0"
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: darkMode ? "#F0F0F0" : "#F0F0F0",
              color: darkMode ? "#333" : "#333", // Icon color changes by theme
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              transition: "all 0.3s ease",
            }}
            onClick={() => {
              // Dispatch Redux action when link is clicked
              dispatch(setSelectedMenu("Notifications"));
            }}
          >
            <i className="bi bi-bell fs-6"></i>
          </Link>

          <button
            className="hi border-0 d-none d-sm-flex"
            onClick={toggleDarkMode}
            title="Toggle Theme"
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: darkMode ? "#F0F0F0" : "#F0F0F0",
              color: darkMode ? "#333" : "#333", // Icon color changes by theme
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0",
            }}
          >
            <i
              className={`bi ${darkMode ? "bi-sun" : "bi-moon"} `}
              style={{ fontSize: "15px" }}
            />
          </button>

          <div className="position-relative" ref={profileRef}>
            <button
              className="p-0 border-0 bg-transparent"
              onClick={toggleProfileMenu}
            >
              {user?.user_profile_pic ? (
                <img
                  src={`${
                    process.env.REACT_APP_SERVER_URL
                  }/${user.user_profile_pic.replace(/\\/g, "/")}`}
                  alt="Profile"
                  className="rounded-circle"
                  style={{ width: "28px", height: "28px", objectFit: "cover" }}
                />
              ) : (
                <div
                  className="rounded-circle bg-dark text-white d-flex align-items-center justify-content-center"
                  style={{ width: "28px", height: "28px", fontSize: "12px" }}
                >
                  {user?.user_name?.charAt(0).toUpperCase() || "U"}
                </div>
              )}
            </button>

            {showProfileMenu && (
              <div
                className="dropdown-menu profile-dropdown show profile-dropdown"
                onMouseLeave={closeMenu}
              >
                {/* <Link
                  className="dropdown-item profile-dropdown-item d-flex align-items-center border-bottom gap-2"
                  to="profile"
                  onClick={() => dispatch(setSelectedMenu("Profile"))}
                > */}
                <Link
                  className="dropdown-item profile-dropdown-item d-flex align-items-center border-bottom gap-2"
                  to="/superadmin/profile"
                  onClick={() => {
                    dispatch(setSelectedMenu("Profile"));
                    closeMenu();
                  }}
                >
                  <img
                    src={profileIcon}
                    alt="Profile Img"
                    className="Profile-Svg icon-image"
                  />{" "}
                  <span className="Profile-text refresh-prompts">Profile</span>
                </Link>

                <button
                  className="dropdown-item profile-dropdown-item d-flex align-items-center gap-2"
                  onClick={handleLogout}
                >
                  <i className="bi bi-box-arrow-right profile-i"></i>{" "}
                  <span className="Profile-text refresh-prompts">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default SuperadminTopbar;
